<template>
  <div class="component" >
    <h1>{{ scopedStrings.title }}</h1>
    <div class="lines-wrapper">
      <!-- If the compilation generates errors, display only errors -->
      <div class="lines" v-if="errors.length > 0">
        <div class="object-code-line-error vertical-pane"
             v-for="error in errors" v-bind:key="error.id">
          <div class="line-no">Line {{error.line}}</div>
          <div class="source">{{error.message}}</div>
        </div>
      </div>

      <!--             Else, display the object code -->
      <div class="lines" v-else>
        <div class="object-code-line" v-for="codeLine in computeHighlightAndTags()" v-bind:key="codeLine.id">
          <div :class="codeLine.class">{{codeLine.line}}</div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
  export default {
    props: {
      scopedStrings: {
        type: Object,
        required: true
      }
    },
    data () {
      return {
        compilationResult : "",
        code : [],
        errors : Array(),
        gutterSize : 2,
        highlightRules : [],
        highlightClasses : [
          "highlightedV1","highlightedV2","highlightedV3","highlightedV4",
          "highlightedV5","highlightedV6","highlightedV7",
          "highlightedV8","highlightedV9","highlightedV10"
        ]
      }
    },
    methods: {
      init (compilationResult, highlightRules) {
        this.compilationResult = compilationResult;
        this.code = compilationResult.output.split('\n');
        this.errors = compilationResult.errors;
        this.highlightRules = highlightRules;
      },
      getCompilationResult() {
        return this.compilationResult;
      },
      isEmpty() {
        return this.code.length == 0;
      },
      reset() {
        this.compilationResult = "";
        this.code = [];
        this.errors = Array();
      },
      /**
       * Inserts the given tag after the first separator pipe ("|") in the code line.
       * If there's not enough spaces in the gutter, the tag will be replaced with a number of "*" equal to the gutter size.
       * @param line the line in the object code on which the tag will be applied
       * @param tag the tag to add in the code line
       * @return the code line containing the given tag
       */
      applyTag(line, tag) {
        if(tag.length > this.gutterSize)
          tag = "*".repeat(this.gutterSize);

        var taggedLine = line.split(/(\|)/);
        var regexp = new RegExp(" {" + tag.length + "}");  // expression used to replace as many spaces as the tag length
        taggedLine[2] = taggedLine[2].replace(regexp, tag);
        return taggedLine.join("");
      },
      /**
       * Builds an array containing codeLines objects. Each object contains the line to display and the CSS class to apply on it.
       *
       * For each line of code, we check if it matches a highlight rule.
       * If it does, we store the CSS class associated with the matched rule as well as the new code line to display in an object.
       * This object is appended to the array to be returned.
       *
       * NOTE :
       * If a line matches several rules, all the matched rules' tags are added and the CSS class
       * corresponding to the first matched rule is stored in the object.
       * If no match occurred, the line isn't modified and the CSS class to be applied is null.
       *
       * @return an array of codeLines objects, each object contains the line to display and the CSS class to use on it
       */
      computeHighlightAndTags() {
        var computedCodeLines = [];
        for(var lineIndex = 0; lineIndex < this.code.length; lineIndex++) {
          var line = this.code[lineIndex];
          // extract the instruction's address from the line
          var addr = line.substring(2, 8).trim();
          // extract the instruction's binary code
          var binary = line.substring(9, 22).trim();

          var highlightClass = null;
          if (addr && binary.trim().length) {
            var addr_dec = parseInt(addr,16);
            var tagToAdd = "";

            // ensure that we're not using more rules than available highlight classes
            for (var index = 0; index < this.highlightRules.length && index < this.highlightClasses.length; index++){
              var rule = this.highlightRules[index];
              if(addr_dec === rule.address) {
                tagToAdd += rule.tag;
                if(highlightClass === null) {
                  highlightClass = this.highlightClasses[index];
                }
              }
            }

            if(tagToAdd != "") {
              line = this.applyTag(line, tagToAdd);
            }
          }
          computedCodeLines.push({"line": line, "class": highlightClass});
        }
        return computedCodeLines;
      },
      updateHighlightRules(newRules) {
        this.highlightRules = newRules;
      }
    }
  }
</script>

<style scoped>
  .lines-wrapper {
    display: flex;
    flex-direction: column;
    background-color: var(--ObjectCode-background);
    height: 200px;
  }

  .lines {
    padding: 8px 0;
    flex: 1 1;
    overflow-x: auto;
    overflow-y: auto;
  }

  .object-code-line,
  .object-code-line-error {
    display: flex;
    font-family: "Monospaced Font";
    font-size: 16px;
    white-space: pre;
  }

  .object-code-line-error {
    color: var(--ObjectCode-error-background);
  }

  .object-code-line-error .line-no {
    flex: 1;
  }

  .object-code-line-error .source {
    flex: 5;
  }

  /* Available CSS classes to use with highlight rules */

  .object-code-line .highlightedV1 {
    background-color: var(--ObjectCode-highlighted-background-1);
    width: 100%;
  }

  .object-code-line .highlightedV2 {
    background-color: var(--ObjectCode-highlighted-background-2);
    width: 100%;
  }

  .object-code-line .highlightedV3 {
    background-color: var(--ObjectCode-highlighted-background-3);
    width: 100%;
  }

  .object-code-line .highlightedV4 {
    background-color: var(--ObjectCode-highlighted-background-4);
    width: 100%;
  }

  .object-code-line .highlightedV5 {
    background-color: var(--ObjectCode-highlighted-background-5);
    width: 100%;
  }

  .object-code-line .highlightedV6 {
    background-color: var(--ObjectCode-highlighted-background-6);
    width: 100%;
  }

  .object-code-line .highlightedV7 {
    background-color: var(--ObjectCode-highlighted-background-7);
    width: 100%;
  }

  .object-code-line .highlightedV8 {
    background-color: var(--ObjectCode-highlighted-background-8);
    width: 100%;
  }

  .object-code-line .highlightedV9 {
    background-color: var(--ObjectCode-highlighted-background-9);
    width: 100%;
  }

  .object-code-line .highlightedV10 {
    background-color: var(--ObjectCode-highlighted-background-10);
    width: 100%;
  }

</style>
